import { orchestrator, action, mutatorAction } from "satcheljs";
import { setAvailableWidthBucket, setAvailableHeightBucket, } from "../utils/initializeDynamicLayout";
import { getBrowserWidth, getBrowserHeight } from "owa-config";
import { onWindowResize } from "./onWindowResize";
import type LayoutChangeSource from "../store/schema/LayoutChangeSource";
import { getStore } from "../store/store";
import type BrowserHeightBucket from "../store/schema/BrowserHeightBucket";
import type BrowserWidthBucket from "../store/schema/BrowserWidthBucket";

/**
 * While we want to generally avoid using orchestrators if the action is defined in the
 * same package, setAvailableHeightAndWidthBuckets has to be an action/orchestrator because we want
 * mobx to batch all of the store updates together, to avoid components rendering multiple times
 * in response to these updates. To ensure that the orchestrator is registered by the time the action is fired,
 * we're defining the actions, mutators, and orchestrator all in the same file. This also makes future updates
 * easier, as all the related code is in one place.
 */
export interface SetAvailableHeightAndWidthBucketsActionMessage {
    layoutChangeSource: LayoutChangeSource;
}
export const setAvailableHeightAndWidthBuckets: (layoutChangeSource: LayoutChangeSource) => SetAvailableHeightAndWidthBucketsActionMessage = action("setAvailableHeightAndWidthBuckets", (layoutChangeSource: LayoutChangeSource): SetAvailableHeightAndWidthBucketsActionMessage => ({
    layoutChangeSource,
}));

orchestrator(setAvailableHeightAndWidthBuckets, actionMessage => {
    const { layoutChangeSource } = actionMessage;
    setAvailableWidthBucket(layoutChangeSource);
    setAvailableHeightBucket(layoutChangeSource);
    // Notify consumers
    onWindowResize(getBrowserHeight(), getBrowserWidth(), layoutChangeSource);
});

/**
 * Sets browser's current height bucket
 * @param browserHeightBucket Browser height bucket
 */
/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const setBrowserHeightBucket: (browserHeightBucket: BrowserHeightBucket) => void = mutatorAction("setBrowserHeightBucket", (browserHeightBucket: BrowserHeightBucket): void => {
    getStore().browserHeightBucket = browserHeightBucket;
});

/**
 * Sets browser's main app min height
 * @param mainAppMinHeight Browser main app min height
 */
export const setMainAppHeightOverflowMutator: (mainAppMinHeight: string, isMainAppHeightOverflow: boolean) => void = mutatorAction("setMainAppMinHeight", (mainAppMinHeight: string, isMainAppHeightOverflow): void => {
    getStore().mainAppMinHeight = mainAppMinHeight;
    getStore().isMainAppHeightOverflow = isMainAppHeightOverflow;
});

/**
 * Sets browser's current width bucket
 * @param browserWidthBucket Browser width bucket
 */
/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const setBrowserWidthBucket: (browserWidthBucket: BrowserWidthBucket) => void = mutatorAction("setBrowserWidthBucket", (browserWidthBucket: BrowserWidthBucket): void => {
    getStore().browserWidthBucket = browserWidthBucket;
});
